import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useReducer,
} from 'react';
import { useHttpClient } from '../../contexts';
import {
  appointmentProposalShareReducer,
  AppointmentProposalShareState,
  initState,
  ShareErrorType,
} from './appointmentProposalShare.reducer';

type ContactInfo = {
  value: string;
  type: string;
};

interface AppointmentProposalShareContextProps {
  isLoading: boolean;
  errors: ShareErrorType[];
  clearErrors: () => void;
  shareCustomerSurvey: (
    contacts: ContactInfo[],
    appointmentId: string
  ) => Promise<void>;
}

const AppointmentProposalShareContext = createContext<
  AppointmentProposalShareContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<AppointmentProposalShareState>;
}>;

export const AppointmentProposalShareProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(appointmentProposalShareReducer, {
    ...initState,
    ...initialState,
  });
  const httpClient = useHttpClient();

  const shareCustomerSurvey = useCallback(
    async (contacts: ContactInfo[], appointmentId: string) => {
      dispatch({ type: 'SHARE_CUSTOMER_SURVEY' });
      try {
        await Promise.all(
          contacts.map((contact) =>
            httpClient.post(
              `/appointments/${appointmentId}/send-data-collection`,
              { email: contact.value, type: contact.type }
            )
          )
        );
        dispatch({
          type: 'SHARE_CUSTOMER_SURVEY_SUCCESS',
        });
      } catch (error) {
        dispatch({
          type: 'SHARE_CUSTOMER_SURVEY_FAILURE',
          payload: { error: { message: `${error}`, showBanner: true } },
        });
        throw error;
      }
    },
    [httpClient]
  );

  const clearErrors: AppointmentProposalShareContextProps['clearErrors'] =
    useCallback(() => dispatch({ type: 'CLEAR_ALL_ERRORS' }), []);

  return (
    <AppointmentProposalShareContext.Provider
      value={{
        isLoading: state.isLoading,
        errors: state.errors,
        clearErrors,
        shareCustomerSurvey,
      }}
    >
      {children}
    </AppointmentProposalShareContext.Provider>
  );
};

export const useAppointmentProposalShare = () => {
  const context = useContext(AppointmentProposalShareContext);
  if (!context) {
    throw new Error(
      'useAppointmentProposalShare must be used within an AppointmentProposalShareProvider'
    );
  }
  return context;
};
